/**
 * ScaffoldModuleStep — ②단계 공통 능력 선택 (queue #642 · docs/biz-specs/project-scaffold.md §6.2).
 *
 * 선택지 원천은 common-showcase 의 기존 `GET /api/eraf/showcase/modules`(`useModuleCatalog`
 * 재사용 · 신규 조회 endpoint 없음). SUPERSEDED(`supersededBy != null`) 는 제외. 전이 프리뷰는
 * `take-this.json`(#639 `deps.codes`) 을 그대로 union(#641 CurationBundlePicker 패턴 재사용 —
 * 신규 그래프 계산 금지).
 *
 * queue #675 §UAT-2026-07-13-R675: 상단에 목적별 "추천 프리셋" 카드 그리드를 추가한다. 클릭 시
 * `selectedCodes` 를 프리셋 코드 집합으로 교체(applyPreset)하고, 이후에는 기존 체크박스로 자유
 * 추가/해제할 수 있다. 카탈로그 로딩/오류 중에는 체크박스 목록과 함께 기존 상태를 유지한다
 * (§R675-3 5상태 표).
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState, ErrorState } from '@ui-kit';
import { useModuleCatalog } from '../../common-showcase/hooks/useModuleCatalog';
import takeThisJson from '../../common-showcase/data/take-this.json';
import type { ModuleNode, TakeThisData } from '../../common-showcase/types/showcase';
import { SCAFFOLD_PRESETS, applyPreset, isPresetActive } from '../data/presets';

const TAKE_THIS_DATA = takeThisJson as unknown as TakeThisData;

interface Props {
  selectedCodes: Set<string>;
  onChangeSelected: (next: Set<string>) => void;
  onNext: () => void;
  onBack: () => void;
}

const groupHeaderStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--color-text-secondary)',
  fontFamily: 'var(--font-family-mono)', textTransform: 'uppercase', letterSpacing: '0.05em',
  marginBottom: 'var(--spacing-2)',
};
const rowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', padding: 'var(--spacing-1) 0',
  fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', cursor: 'pointer',
};
const chipStyle: React.CSSProperties = {
  display: 'inline-block', fontSize: 'var(--font-size-xs)', fontFamily: 'var(--font-family-mono)',
  padding: '1px var(--spacing-2)', borderRadius: 'var(--radius-full)', border: '1px solid var(--color-surface-border)',
  background: 'var(--color-surface-elevated)', color: 'var(--color-text-secondary)',
};
const chipTransitiveStyle: React.CSSProperties = {
  ...chipStyle, borderStyle: 'dashed', borderColor: 'var(--color-info)', color: 'var(--color-info)',
};
const btnGhost: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)',
  fontWeight: 600, cursor: 'pointer', border: '1px solid var(--color-surface-border)', background: 'transparent',
  color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-sans)',
};
const btnPrimary: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)',
  fontWeight: 600, cursor: 'pointer', border: 'none', background: 'var(--color-brand-primary)',
  color: 'var(--color-text-inverse)', fontFamily: 'var(--font-family-sans)',
};
const presetGridStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 'var(--spacing-3)',
};
const presetCardStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)', textAlign: 'left',
  padding: 'var(--spacing-3) var(--spacing-4)', borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-surface-border)', background: 'var(--color-surface-elevated)',
  cursor: 'pointer', fontFamily: 'var(--font-family-sans)',
};
const presetCardActiveStyle: React.CSSProperties = {
  ...presetCardStyle, border: '2px solid var(--color-brand-primary)',
};
const presetBadgeStyle: React.CSSProperties = {
  alignSelf: 'flex-start', fontSize: 10, fontWeight: 700, padding: '1px var(--spacing-2)',
  borderRadius: 'var(--radius-full)', color: 'var(--color-brand-primary)', border: '1px solid var(--color-brand-primary)',
};

export function ScaffoldModuleStep({ selectedCodes, onChangeSelected, onNext, onBack }: Props) {
  const { t } = useTranslation();
  const { data, isLoading, isError, error, refetch } = useModuleCatalog();
  const [query, setQuery] = useState('');

  const activeNodes = useMemo(
    () => (data?.nodes ?? []).filter((n: ModuleNode) => n.supersededBy == null),
    [data]
  );

  const filteredNodes = useMemo(() => {
    if (!query.trim()) return activeNodes;
    const q = query.toLowerCase();
    return activeNodes.filter(
      (n) => n.code.toLowerCase().includes(q) || n.nameKo.toLowerCase().includes(q) || n.nameEn.toLowerCase().includes(q)
    );
  }, [activeNodes, query]);

  const grouped = useMemo(() => {
    const map = new Map<string, ModuleNode[]>();
    for (const n of filteredNodes) {
      if (!map.has(n.artifact)) map.set(n.artifact, []);
      map.get(n.artifact)!.push(n);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [filteredNodes]);

  // #639/#641 패턴: 선택 코드별 deps.codes union — 이미 선택된 코드는 전이 카운트에서 제외.
  const transitiveCodes = useMemo(() => {
    const result = new Set<string>();
    selectedCodes.forEach((code) => {
      const entry = TAKE_THIS_DATA[code];
      entry?.deps.codes.forEach((c) => {
        if (!selectedCodes.has(c)) result.add(c);
      });
    });
    return result;
  }, [selectedCodes]);

  function toggle(code: string) {
    const next = new Set(selectedCodes);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    onChangeSelected(next);
  }

  function isHardInject(code: string): boolean {
    const entry = TAKE_THIS_DATA[code];
    return !!entry && !entry.yml.alwaysOn && !!entry.yml.hardInject;
  }

  const isEmpty = selectedCodes.size === 0;

  return (
    <div style={{ background: 'var(--color-surface-muted)', border: '1px solid var(--color-surface-border)', borderRadius: 'var(--radius-lg)', padding: 'var(--spacing-6)' }}>
      <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: 'var(--spacing-1)' }}>
        {`② ${t('projectScaffold.step.modules')}`}
      </p>
      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-4)' }}>
        {t('projectScaffold.step.modulesDesc')}
      </p>

      {/* queue #675 §R675-3: 추천 프리셋 카드 그리드 — 카탈로그 로딩/오류 중에는 미노출(체크박스 목록과 동시 등장) */}
      {!isLoading && !isError && (
        <div
          role="group"
          aria-label={t('projectScaffold.preset.groupAriaLabel')}
          style={{ marginBottom: 'var(--spacing-4)', padding: 'var(--spacing-3) var(--spacing-4)', border: '1px solid var(--color-surface-border)', borderRadius: 'var(--radius-md)', background: 'var(--color-surface-muted)' }}
        >
          <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: 'var(--spacing-1)' }}>
            {t('projectScaffold.preset.groupTitle')}
          </p>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-3)' }}>
            {t('projectScaffold.preset.groupDesc')}
          </p>
          <div style={presetGridStyle}>
            {SCAFFOLD_PRESETS.map((preset) => {
              const active = isPresetActive(preset, selectedCodes);
              const presetName = t(preset.nameKey);
              return (
                <button
                  key={preset.id}
                  type="button"
                  data-testid={`scaffold-preset-${preset.id}`}
                  aria-pressed={active}
                  aria-label={t('projectScaffold.preset.applyAria', { name: presetName })}
                  onClick={() => onChangeSelected(applyPreset(preset))}
                  style={active ? presetCardActiveStyle : presetCardStyle}
                >
                  {active && <span style={presetBadgeStyle}>{t('projectScaffold.preset.activeBadge')}</span>}
                  <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--color-text-primary)' }}>{presetName}</span>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>{t(preset.summaryKey)}</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-1)' }}>
                    {preset.codes.map((c) => <span key={c} style={chipStyle}>{c}</span>)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div role="search" style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center', marginBottom: 'var(--spacing-4)', padding: 'var(--spacing-2) var(--spacing-3)', background: 'var(--color-surface-elevated)', border: '1px solid var(--color-surface-border)', borderRadius: 'var(--radius-md)' }}>
        <span aria-hidden="true">🔍</span>
        <input
          type="search"
          placeholder={t('projectScaffold.search.placeholder')}
          aria-label={t('projectScaffold.search.ariaLabel')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-sans)' }}
        />
      </div>

      {isLoading && (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>{t('projectScaffold.preview.loading')}</p>
      )}

      {isError && (
        <ErrorState title={t('projectScaffold.error.catalogTitle')} message={error?.message} onRetry={() => { void refetch(); }} />
      )}

      {!isLoading && !isError && (
        <>
          <div
            role="group"
            aria-label={t('projectScaffold.module.groupAriaLabel')}
            style={{ border: '1px solid var(--color-surface-border)', borderRadius: 'var(--radius-md)', background: 'var(--color-surface-elevated)', maxHeight: 320, overflowY: 'auto', padding: 'var(--spacing-3) var(--spacing-4)' }}
          >
            {grouped.map(([artifact, nodes]) => (
              <div key={artifact} style={{ marginBottom: 'var(--spacing-4)' }}>
                <p style={groupHeaderStyle}>{artifact}</p>
                {nodes.map((n) => (
                  <label key={n.code} style={rowStyle}>
                    <input type="checkbox" checked={selectedCodes.has(n.code)} onChange={() => toggle(n.code)} style={{ accentColor: 'var(--color-brand-primary)', width: 15, height: 15 }} />
                    <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-brand-primary)', minWidth: 42 }}>{n.code}</span>
                    <span>{n.nameKo}</span>
                    {isHardInject(n.code) && (
                      <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, padding: '1px var(--spacing-2)', borderRadius: 'var(--radius-full)', color: 'var(--color-warning)', border: '1px solid var(--color-warning)' }}>
                        {t('projectScaffold.module.hardInjectBadge')}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            ))}
          </div>

          {isEmpty ? (
            <EmptyState
              title={t('projectScaffold.emptyState.title')}
              description={t('projectScaffold.emptyState.desc')}
            />
          ) : (
            <div
              aria-live="polite"
              style={{
                display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', flexWrap: 'wrap', marginTop: 'var(--spacing-4)',
                padding: 'var(--spacing-3) var(--spacing-4)', background: 'color-mix(in srgb, var(--color-brand-primary) 8%, transparent)',
                border: '1px solid var(--color-brand-primary)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)',
              }}
            >
              <span>
                {t('projectScaffold.summary.count', {
                  selected: selectedCodes.size,
                  transitive: transitiveCodes.size,
                  total: selectedCodes.size + transitiveCodes.size,
                })}
              </span>
              {[...selectedCodes].sort().map((c) => <span key={c} style={chipStyle}>{c}</span>)}
              {[...transitiveCodes].sort().map((c) => (
                <span key={c} style={chipTransitiveStyle}>{t('projectScaffold.summary.transitiveChip', { code: c })}</span>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-6)', flexWrap: 'wrap' }}>
            <button type="button" onClick={onBack} style={btnGhost}>{t('projectScaffold.step.back')}</button>
            <button type="button" onClick={onNext} style={btnPrimary}>
              {isEmpty ? t('projectScaffold.step.modulesNextEmpty') : t('projectScaffold.step.modulesNext')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
